import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import CumeoMatch from "../CumeoMatch";
import CumeoDateTabs from "../CumeoDateTabs";
import LoadMoreButton from "../LoadMoreButton";
import CumeoOddsAndBookmaker from "../CumeoOddsAndBookmaker";
import LeagueFilterV14 from "../LeagueFilterV14";
import { BANG_KEO_PAGE_LIMIT, BANKER, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import {
  CHANGE_BOOKMAKER,
  CHANGE_BOOKMAKER_EXTEND,
} from "@/lib/redux/reducers/actionTypes";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV14 = ({ type = "all" }) => {

  const {
    colorPrimary,
    colorSecondary,
    colorBg,
    colorText,
    colorForeground,
    colorBorder,
    colorAccent,
    colorTextMuted,
    colorTextSecondary,
    colorOnPrimary,
    colorBgHover,
    colorHalfScore,
    colorGradientStart,
    colorGradientEnd,
    colorLiveAlt,
    hasDataConfig,
  } = useCompConfig();

  const themeStyle = hasDataConfig
    ? {
        "--theme-primary": colorPrimary,
        "--theme-highlight": colorSecondary,
        "--theme-bg": colorBg,
        "--theme-text": colorText,
        "--theme-foreground": colorForeground,
        ...(colorBorder != null && { "--theme-border": colorBorder }),
        ...(colorAccent != null && { "--theme-accent": colorAccent }),
        ...(colorTextMuted != null && { "--theme-text-muted": colorTextMuted }),
        ...(colorTextSecondary != null && { "--theme-text-secondary": colorTextSecondary }),
        ...(colorOnPrimary != null && { "--theme-on-primary": colorOnPrimary }),
        ...(colorBgHover != null && { "--theme-bg-hover": colorBgHover }),
        ...(colorHalfScore != null && { "--text-half-score": colorHalfScore }),
        ...(colorGradientStart != null && { "--theme-gradient-start": colorGradientStart }),
        ...(colorGradientEnd != null && { "--theme-gradient-end": colorGradientEnd }),
        ...(colorLiveAlt != null && { "--theme-live-alt": colorLiveAlt }),
      }
    : {};

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayString);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(20);

  const dispatch = useDispatch();
  const bookmarkerExtend = useSelector(
    (state) => state.bookmarkerExtendReducer,
  );
  const bookmarker = useSelector((state) => state.bookmakerReducer);

  const bookmakerOptions = useMemo(
    () => [
      { value: "-1", label: "Tất cả" },
      ...BANKER.map((item) => ({ value: item.value, label: item.name })),
    ],
    [],
  );

  const bookmakerExtendOptions = useMemo(
    () =>
      BANKER_EXTENDS.map((item) => ({
        value: item.value,
        label: `Tỷ lệ ${item.shortName}`,
      })),
    [],
  );


  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status =
    type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    const idSet = new Set(selectedLeagueIds.map(String));
    return baseLeaguesWithMatches.filter(({ league }) =>
      idSet.has(String(league?.id)),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  // For display limit, we still use a flat list for calculation
  const allFilteredMatches = useMemo(() => {
    return filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [filteredLeaguesWithMatches]);

  useEffect(() => {
    if (
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [
    firstLoad,
    selectedDate,
    loading,
    filteredLeaguesWithMatches,
    todayString,
  ]);

  const handleLeagueFilterChange = (ids) => {
    setSelectedLeagueIds(ids);
    setDisplayLimit(20);
  };

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setFirstLoad(false);
    setDisplayLimit(20);
  };

  return (
    <div
      data-theme="orange-dark-slate"
      className="max-w-[1280px] mx-auto flex min-h-screen flex-col gap-3 rounded-xl px-2 md:px-2 py-3 bg-[var(--theme-bg)] box-border"
      style={{
        ...themeStyle,
      }}
    >

      <div className="flex flex-col gap-3 m-0 p-0 box-border">
        <div className="relative overflow-visible mr-0.5 mb-4 box-border w-full rounded-xl bg-[var(--kbv-card-bg)] max-md:mb-0 max-md:min-h-[49px] max-md:rounded-t-[14px] max-md:rounded-b-none max-md:shadow-none max-md:px-0 md:max-[1056px]:min-h-[56px] md:max-[1056px]:px-5 min-[1057px]:min-h-16">
          <div className="flex h-full w-full min-w-0 gap-2 flex-col py-2 md:flex-row md:items-center md:gap-x-3 md:py-0">
            <div className="flex min-w-0 flex-1 items-center max-md:mt-0 mt-[4px] !box-border max-w-full overflow-x-auto overflow-y-hidden md:overflow-x-visible md:overflow-hidden">
              <CumeoDateTabs
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                compact
              />
            </div>
            <div className="flex w-full min-w-0 md:w-auto md:shrink-0 md:justify-end">
              <CumeoOddsAndBookmaker
                extendValue={bookmarkerExtend}
                bookmakerValue={bookmarker}
                extendOptions={bookmakerExtendOptions}
                bookmakerOptions={bookmakerOptions}
                onExtendChange={(val) =>
                  dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
                }
                onBookmakerChange={(val) =>
                  dispatch({ type: CHANGE_BOOKMAKER, payload: val })
                }
                extendPlaceholder="Tỷ lệ HONGKONG"
                bookmakerPlaceholder="Tất cả"
              />
            </div>
            <div className="flex w-full min-w-0 items-center justify-center md:w-[220px] md:shrink-0 md:justify-end">
              <LeagueFilterV14
                selectedIds={selectedLeagueIds}
                onChange={handleLeagueFilterChange}
                className="h-full w-full"
                themeStyle={themeStyle}
              />
            </div>
          </div>
        </div>

        {loading && <Loading />}
        {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
        {!loading && (
          <>
            <div className="w-full bg-[var(--theme-foreground)] overflow-hidden rounded-md border border-solid border-[var(--theme-border)] m-0 p-0 box-border border-0">
                <div className="hidden md:grid grid-cols-[70px_2.5fr_5fr_6fr] gap-4 px-2 py-3 text-xs font-semibold text-[var(--theme-text)] uppercase border-b  border-r border-l border-t border-solid border-[var(--theme-border)] items-center bg-[var(--theme-accent)] m-0 box-border border-0">
                  <div className="m-0 p-0 text-start">Thời Gian</div>
                  <div className="m-0 p-0">Giải Đấu</div>
                  <div className="m-0 p-0">Trận Đấu</div>
                  <div className="grid grid-cols-3 gap-0.5 m-0 p-0 box-border">
                    <div className="text-center m-0 p-0">Cược Chấp</div>
                    <div className="text-center m-0 p-0">Tài/Xỉu</div>
                    <div className="text-center m-0 p-0">1X2</div>
                  </div>
                </div>

                <div className="flex flex-col m-0 p-0">
                  {allFilteredMatches.length === 0 ? (
                    <div className="p-10">
                        <NoData
                          message={
                            isLoadingMore
                              ? "Đang tìm thêm trận đấu phù hợp..."
                              : "Không tìm thấy trận đấu theo tiêu chí lọc"
                          }
                        />
                    </div>
                  ) : (
                    allFilteredMatches.map((match, mIdx) => (
                      <CumeoMatch
                        key={match?.id || `match-${mIdx}`}
                        match={match}
                      />
                    ))
                  )}
                </div>
            </div>
            
            <LoadMoreButton
              isLoading={isLoadingMore}
              onClick={() => setDisplayLimit((prev) => prev + 20)}
              loadingComponent={<Loading />}
              hasMore={allFilteredMatches.length > displayLimit || hasMore}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default KbvTiLeV14;
